import moment from "moment";

import type { AxiosResponse } from "axios";

import { getData, postData } from "@/apis/apiInstance";

import type {
  AadhaarAndPanUniqueCheckPayloadType,
  AddClientDirectoryPayloadType,
  AddLeadModuleCommentPayload,
  ChangeClientStatusChangePayloadType,
  DownloadClientDirectoryReportType,
  EmailHistoryPayload,
  GetCityListPayloadType,
  GetClientDirectoryListPayloadType,
  GetClientMeetUpChecklistPayloadType,
  GetHistoryPayload,
  GetLeadDirectoryListPayloadType,
  GetLeadModuleCommentsPayload,
  GetSingleClientDirectoryPayloadType,
  GetSingleClientMeetUpChecklistPayloadType,
  MeetingChecklistPayload,
  SendEmailPayload,
  SingleClientLeadDirectoryDetails,
  StageInfoPayloadType,
  UpdateClientDirectoryPayloadType,
  UpdateSingleClientLeadDirectoryDetails,
} from "@/commonTypes/clientDirectoryTypes";

import { formatDate, getCSRFToken, reverseDate } from "@/utils/commonFunctions";

export async function getClientDirectoryApi(
  data: GetClientDirectoryListPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    searchValue,
    limit,
    offset,
    name,
    clientCode,
    contact_type,
    clientFrom,
    status,
    isView,
    groupId,
    client_category,
    scope_of_work,
    branch_id,
    signup_filter,
    is_signup_from_mobile,
  } = data;
  return await getData(
    isView
      ? `master-module/user/client-directory`
      : `master-module/customer/list`,
    {
      params: {
        search_term: searchValue,
        limit: limit,
        offset: offset,
        name_sorted_order: name,
        client_from_sorted_order: clientFrom,
        client_code_sorted_order: clientCode,
        ...(contact_type && {
          contact_type: contact_type ? contact_type : "",
        }),
        status,
        ...(client_category && {
          client_category: client_category ? client_category : "",
        }),
        ...(scope_of_work && {
          scope_of_work: scope_of_work ? scope_of_work : "",
        }),
        ...(branch_id && {
          branch_id: branch_id ? branch_id : "",
        }),
        ...(signup_filter && {
          signup_filter: signup_filter ? signup_filter : "",
        }),
        ...(is_signup_from_mobile != null
          ? {
              is_signup_from_mobile: is_signup_from_mobile,
            }
          : {}),
      },
      headers: {
        "Content-Type": "application/json",
        "user-token": token,
      },
    }
  );
}
export async function getSingleClientDirectoryApi(
  data: GetSingleClientDirectoryPayloadType
): Promise<AxiosResponse> {
  const { token, clientCode } = data;
  return await getData(`master-module/customer`, {
    params: {
      client_code: clientCode,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function getClientMeetUpChecklistApi(
  data: GetClientMeetUpChecklistPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    role,
    limit,
    offset,
    searchData,
    sortBy,
    sortOrder,
    meetingAssignedTo,
    leadAssignedTo,
    meetUpCompleted,
  } = data;
  return await getData(`finac-crm/lead/meet-up/checklist/list`, {
    params: {
      role,
      limit,
      offset,
      search_term: searchData,
      sort_by: sortBy,
      sorting_order: sortOrder,
      meeting_assigned_to: meetingAssignedTo,
      lead_assigned_to: leadAssignedTo,
      meet_up_completed: meetUpCompleted,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function getSingleClientMeetUpChecklistApi(
  data: GetSingleClientMeetUpChecklistPayloadType
): Promise<AxiosResponse> {
  const { token, clientCode } = data;
  return await getData(`finac-crm/lead/meet-up/checklist/details`, {
    params: {
      client_code: clientCode,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function getCityListApi(
  data: GetCityListPayloadType
): Promise<AxiosResponse> {
  const { token, limit, offset, searchTerm } = data;
  return await getData(`finac-crm/client-lead/unqiue/cities`, {
    params: {
      limit,
      offset,
      search_term: searchTerm,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function getStageListApi(
  data: GetCityListPayloadType
): Promise<AxiosResponse> {
  const { token, limit, offset, searchTerm } = data;
  return await getData(`finac-crm/lead/stage/list`, {
    params: {
      limit,
      offset,
      search_term: searchTerm,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function updateLeadStatusApi(
  data: ChangeClientStatusChangePayloadType
): Promise<AxiosResponse> {
  const { token, clientCode, status } = data;
  const finalData = {
    client_code: clientCode,
    status,
  };
  return await postData(`finac-crm/lead/status/update`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function updateLeadStageApi(
  data: StageInfoPayloadType
): Promise<AxiosResponse> {
  const { token, stageId, stageName, thresholdDay, uniqueId } = data;
  const finalData = {
    stage_id: stageId,
    stage_name: stageName,
    threshold_days: thresholdDay,
    unique_id: uniqueId,
  };
  return await postData(`finac-crm/lead/stage/update`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function changeClientStatusApi(
  data: ChangeClientStatusChangePayloadType
): Promise<AxiosResponse> {
  const { token, clientCode, status } = data;
  const finalData = {
    client_code: clientCode,
    status,
  };
  return await postData(`master-module/change/customer/status`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function clientAadharAndPanUniqueCheckApi(
  data: AadhaarAndPanUniqueCheckPayloadType
): Promise<AxiosResponse> {
  const { token, operation, role, clientCode, aadhaarNumber, panNumber } = data;
  const finalData = {
    operation,
    role,
    client_code: clientCode,
    aadhaar: aadhaarNumber,
    pan: panNumber,
  };
  return await getData(`master-module/aadhaar-pan/unique/check`, {
    params: finalData,
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function updateClientDirectoryApi(
  data: UpdateClientDirectoryPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    status,
    clientCode,
    comment_count,
    lastComment,
    leadInfoDetails,
    profileSetupDetails,
    registrationsInputDetails,
    taxAndBillingDetails,
    clientBasicDetails,
    migrateToFinac,
  } = data;
  const currencyDetails = {
    id: clientBasicDetails?.assignedTo?.currency?.currencyDetails?.currencyId
      ? clientBasicDetails?.assignedTo?.currency?.currencyDetails?.currencyId
      : "",
    name: clientBasicDetails?.assignedTo?.currency?.currencyDetails
      ?.currencyName
      ? clientBasicDetails?.assignedTo?.currency?.currencyDetails?.currencyName
      : "",
    sign: clientBasicDetails?.assignedTo?.currency?.currencyDetails
      ?.currencySign
      ? clientBasicDetails?.assignedTo?.currency?.currencyDetails?.currencySign
      : "",
    short_form: clientBasicDetails?.assignedTo?.currency?.currencyDetails
      ?.currencyShortForm
      ? clientBasicDetails?.assignedTo?.currency?.currencyDetails
          ?.currencyShortForm
      : "",
  };

  const finalData = {
    client_code: clientCode,
    status,
    user_details: {
      profile_pic: clientBasicDetails?.basicDetails?.profilePic,
      basic_details: {
        first_name:
          clientBasicDetails?.basicDetails?.entity?.value === "Individual" ||
          clientBasicDetails?.basicDetails?.entity?.value ===
            "Sole Proprietorship"
            ? clientBasicDetails?.basicDetails?.firstName?.trim()
            : "",
        last_name:
          clientBasicDetails?.basicDetails?.entity?.value === "Individual" ||
          clientBasicDetails?.basicDetails?.entity?.value ===
            "Sole Proprietorship"
            ? clientBasicDetails?.basicDetails?.lastName?.trim()
            : "",
        name:
          clientBasicDetails?.basicDetails?.entity?.value === "Individual" ||
          clientBasicDetails?.basicDetails?.entity?.value ===
            "Sole Proprietorship"
            ? `${
                clientBasicDetails?.basicDetails?.lastName
                  ? clientBasicDetails?.basicDetails?.lastName?.trim() + ", "
                  : ""
              }${clientBasicDetails?.basicDetails?.firstName?.trim()}`
            : clientBasicDetails?.basicDetails?.name?.trim(),
        entity: clientBasicDetails?.basicDetails?.entity?.value,
        contact_type: clientBasicDetails?.basicDetails?.contactType?.value,
        mailing_name: clientBasicDetails?.basicDetails?.mailingName,
        salutation: clientBasicDetails?.basicDetails?.salutation,
        client_category: clientBasicDetails?.basicDetails?.clientCategory,
      },
      assigned_to_details: {
        partner: clientBasicDetails?.assignedTo?.partner?.value,
        manager: clientBasicDetails?.assignedTo?.manager?.value,
        senior_manager: clientBasicDetails?.assignedTo?.seniorManager?.value,
        currency: currencyDetails,
        accounting_in_charge:
          clientBasicDetails?.assignedTo?.accountingInCharge?.value,
        tax_in_charge: clientBasicDetails?.assignedTo?.taxInCharge?.value,
        audit_in_charge: clientBasicDetails?.assignedTo?.auditInCharge?.value,
        hr_in_charge: clientBasicDetails?.assignedTo?.hrInCharge?.value,
        branch: {
          name:
            clientBasicDetails?.assignedTo?.branch?.label === "Select.."
              ? ""
              : clientBasicDetails?.assignedTo?.branch?.label,
          id: clientBasicDetails?.assignedTo?.branch?.value,
        },
        client_group: {
          client_group_name:
            clientBasicDetails?.assignedTo?.clientGroup?.label === "Select.."
              ? ""
              : clientBasicDetails?.assignedTo?.clientGroup?.label,
          id: clientBasicDetails?.assignedTo?.clientGroup?.value,
        },
      },
      postal_address_details: {
        address_line1: clientBasicDetails?.postalAddress?.addressLine1,
        address_line2: clientBasicDetails?.postalAddress?.addressLine2,
        address: clientBasicDetails?.postalAddress?.address,
        city: clientBasicDetails?.postalAddress?.city?.label,
        state: clientBasicDetails?.postalAddress?.state?.label,
        pincode: clientBasicDetails?.postalAddress?.postalCode,
        country: clientBasicDetails?.postalAddress?.country?.label,
      },
      contact_details: {
        email: clientBasicDetails?.communication?.email?.trim(),
        mobile: clientBasicDetails?.communication?.mobile,
        home_phone: clientBasicDetails?.communication?.homePhone,
        work_phone: clientBasicDetails?.communication?.workPhone,
        fax: clientBasicDetails?.communication?.fax,
      },
      online_presence: {
        skype: clientBasicDetails?.onlinePresence?.skype,
        website: clientBasicDetails?.onlinePresence?.website,
        twitter: clientBasicDetails?.onlinePresence?.twitter,
        linkedin: clientBasicDetails?.onlinePresence?.linkedIn,
      },
    },
    registration_details: {
      corporate_id:
        registrationsInputDetails?.registrationDetails?.corporateIdOfTheCompany,
      director_of_partner_id:
        registrationsInputDetails?.registrationDetails?.directorOfPartnerId,
      tan: registrationsInputDetails?.registrationDetails?.tan,
      tan_details: registrationsInputDetails?.registrationDetails?.tanDetails,
      pan: registrationsInputDetails?.registrationDetails?.pan,
      pan_details: registrationsInputDetails?.registrationDetails?.panDetails,
      tin: registrationsInputDetails?.registrationDetails?.tin,
      service_tax_number:
        registrationsInputDetails?.registrationDetails?.serviceTaxNumber,
      gst_number: registrationsInputDetails?.gstDetails?.gstNumber,
      legal_name: registrationsInputDetails?.gstDetails?.legalName,
      trade_name: registrationsInputDetails?.gstDetails?.tradeName,
      registration_type:
        registrationsInputDetails?.gstDetails?.registrationType,
      registration_date:
        registrationsInputDetails?.gstDetails?.registrationDate,
      constituion_of_business:
        registrationsInputDetails?.gstDetails?.constitutionOfBusiness,
      company_status: registrationsInputDetails?.gstDetails?.companyStatus,
      registered_under_details: {
        iec_code: registrationsInputDetails?.registerdUnder?.iecCode,
        msme_udhyam_registration:
          registrationsInputDetails?.registerdUnder?.msmeUdhyamRegistration,
        startupindia: registrationsInputDetails?.registerdUnder?.startupIndia,
        iac_80_application:
          registrationsInputDetails?.registerdUnder?.iacApplication,
        gumasta: registrationsInputDetails?.registerdUnder?.gumasta,
        trademark: registrationsInputDetails?.registerdUnder?.trademark,
      },
      labour_law_details: {
        professional_tax_employee:
          registrationsInputDetails?.labourLaw?.professionalTaxEmployee,
        professional_tax_employer:
          registrationsInputDetails?.labourLaw?.professionalTaxEmployer,
        epfo: registrationsInputDetails?.labourLaw?.epfo,
      },
      others_details: {
        black_listed:
          registrationsInputDetails?.others?.blackListed?.value === "Yes"
            ? true
            : false,
        no_of_employees: registrationsInputDetails?.others?.numberOfEmployees,
        turnover_in_crores: registrationsInputDetails?.others?.turnoverInCrores,
        lie: registrationsInputDetails?.others?.lie,
        lin: registrationsInputDetails?.others?.lin,
        adhar_number: registrationsInputDetails?.others?.aadhaarNo,
        category_of_client:
          registrationsInputDetails?.others?.categoryOfClient?.value,
        business_nature: registrationsInputDetails?.others?.businessNature,
      },
    },
    tax_and_billing_details: {
      service_tax_details: {
        service_tax_registered:
          taxAndBillingDetails?.serviceDetails?.serviceTaxRegistered?.value ===
          "Yes"
            ? true
            : false,
        service_tax_period:
          taxAndBillingDetails?.serviceDetails?.serviceTaxPeriod?.value,
        service_tax_basis:
          taxAndBillingDetails?.serviceDetails?.serviceTaxBasis?.value,
        prepare_service_tax:
          taxAndBillingDetails?.serviceDetails?.prepareServiceTax?.value ===
          "Yes"
            ? true
            : false,
      },
      billing_details: {
        billing_entity:
          taxAndBillingDetails?.billingDetails?.billingEntity?.value === "Yes"
            ? true
            : false,
        charge_interest:
          taxAndBillingDetails?.billingDetails?.chargeInterest?.value === "Yes"
            ? true
            : false,
        interest_rate: taxAndBillingDetails?.billingDetails?.interestRate,
        target_hours: taxAndBillingDetails?.billingDetails?.targetHours,
        interest_period:
          taxAndBillingDetails?.billingDetails?.interestPeriod?.value,
        charge_monthly_disbursement:
          taxAndBillingDetails?.billingDetails?.chargeMonthlyDisbursement
            ?.value === "Yes"
            ? true
            : false,
        send_invoice_reminder:
          taxAndBillingDetails?.billingDetails?.sendInvoiceReminder?.value ===
          "Yes"
            ? true
            : false,
      },
    },
    profile_setup_details: {
      tax_year_end: profileSetupDetails?.profileSetup?.taxYearEnd?.value,
      client_type: profileSetupDetails?.profileSetup?.clientType?.value,
      clinet_type_subcategory:
        profileSetupDetails?.profileSetup?.clientTypeSubcategory?.value,
      no_of_employees: profileSetupDetails?.profileSetup?.noOfEmployees,
      in_business_since: profileSetupDetails?.profileSetup?.inBusinessSince
        ? formatDate(profileSetupDetails?.profileSetup?.inBusinessSince)
        : null,
      annual_accounts_month: profileSetupDetails?.annualAccountsMonth?.value,
      client_history: {
        client_from: profileSetupDetails?.clientHistory?.clientFrom
          ? formatDate(profileSetupDetails?.clientHistory?.clientFrom)
          : null,
        client_until: profileSetupDetails?.clientHistory?.clientUntil
          ? formatDate(profileSetupDetails?.clientHistory?.clientUntil)
          : null,
      },
    },
    lead_info_details: {
      comment_count,
      finac_migration_status: migrateToFinac,
      last_comment: lastComment,
      lead_received_on: leadInfoDetails?.leadInfo?.leadReceivedOn
        ? formatDate(leadInfoDetails?.leadInfo?.leadReceivedOn)
        : null,
      lead_assigned_to: leadInfoDetails?.leadInfo?.leadAssignedTo?.value,
      demo_assigned_to: leadInfoDetails?.leadInfo?.demoAssignedTo?.value,
      meeting_assigned_to: leadInfoDetails?.leadInfo?.meetingAssignedTo?.value,
      meeting_time: leadInfoDetails?.leadInfo?.meetingTimewithDate
        ? moment(
            leadInfoDetails?.leadInfo?.meetingTimewithDate?.split(",").join("")
          ).format("DD-MM-YYYY, HH:mm:ss")
        : null,
      follow_up_date: leadInfoDetails?.leadInfo?.followUpDateWithTime
        ? moment(
            leadInfoDetails?.leadInfo?.followUpDateWithTime?.split(",").join("")
          ).format("DD-MM-YYYY, HH:mm:ss")
        : null,
      access_member_ids: leadInfoDetails?.leadInfo?.accessTeamMembersId,
      lead_details: {
        response: leadInfoDetails?.leadDetails?.response,
        stage: leadInfoDetails?.leadDetails?.stage?.value,
        lead_category: leadInfoDetails?.leadDetails?.leadCategary?.value,
        call_frequency: leadInfoDetails?.leadDetails?.callFrequency?.value
          ? leadInfoDetails?.leadDetails?.callFrequency?.value
          : "",
        source: leadInfoDetails?.leadDetails?.source,
        feedback: leadInfoDetails?.leadDetails?.feedback,
        lead_stage_unique_id: leadInfoDetails?.leadDetails?.uniqueId,
      },
      status: leadInfoDetails?.leadDetails?.status,
      company_details: {
        company_name: leadInfoDetails?.companyDetails?.companyName,
        contact_person_name: leadInfoDetails?.companyDetails?.contactPersonName,
        contact_number: leadInfoDetails?.companyDetails?.contactNumber,
        email: leadInfoDetails?.companyDetails?.emailId,
        gst_number_registered:
          leadInfoDetails?.companyDetails?.gstNumberRegister,
      },
      scope_of_work_details: {
        scope_of_work: leadInfoDetails?.scopeOfWorkDetails?.scopeOfWork,
      },
      attachments: leadInfoDetails?.attachments,
    },
  };
  return await postData(`master-module/customer/update`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}
export async function addClientDirectoryApi(
  data: AddClientDirectoryPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    leadInfoDetails,
    profileSetupDetails,
    registrationsInputDetails,
    taxAndBillingDetails,
    clientBasicDetails,
  } = data;
  const currencyDetails = {
    id: clientBasicDetails?.assignedTo?.currency?.currencyDetails?.currencyId
      ? clientBasicDetails?.assignedTo?.currency?.currencyDetails?.currencyId
      : "",
    name: clientBasicDetails?.assignedTo?.currency?.currencyDetails
      ?.currencyName
      ? clientBasicDetails?.assignedTo?.currency?.currencyDetails?.currencyName
      : "",
    sign: clientBasicDetails?.assignedTo?.currency?.currencyDetails
      ?.currencySign
      ? clientBasicDetails?.assignedTo?.currency?.currencyDetails?.currencySign
      : "",
    short_form: clientBasicDetails?.assignedTo?.currency?.currencyDetails
      ?.currencyShortForm
      ? clientBasicDetails?.assignedTo?.currency?.currencyDetails
          ?.currencyShortForm
      : "",
  };
  const finalData = {
    user_details: {
      profile_pic: clientBasicDetails?.basicDetails?.profilePic,
      basic_details: {
        first_name:
          clientBasicDetails?.basicDetails?.entity?.value === "Individual" ||
          clientBasicDetails?.basicDetails?.entity?.value ===
            "Sole Proprietorship"
            ? clientBasicDetails?.basicDetails?.firstName?.trim()
            : "",
        last_name:
          clientBasicDetails?.basicDetails?.entity?.value === "Individual" ||
          clientBasicDetails?.basicDetails?.entity?.value ===
            "Sole Proprietorship"
            ? clientBasicDetails?.basicDetails?.lastName?.trim()
            : "",
        name:
          clientBasicDetails?.basicDetails?.entity?.value === "Individual" ||
          clientBasicDetails?.basicDetails?.entity?.value ===
            "Sole Proprietorship"
            ? `${
                clientBasicDetails?.basicDetails?.lastName
                  ? clientBasicDetails?.basicDetails?.lastName?.trim() + ", "
                  : ""
              }${clientBasicDetails?.basicDetails?.firstName?.trim()}`
            : clientBasicDetails?.basicDetails?.name?.trim(),
        entity: clientBasicDetails?.basicDetails?.entity?.value,
        contact_type: clientBasicDetails?.basicDetails?.contactType?.value,
        mailing_name: clientBasicDetails?.basicDetails?.mailingName,
        salutation: clientBasicDetails?.basicDetails?.salutation,
        client_category: clientBasicDetails?.basicDetails?.clientCategory,
      },
      assigned_to_details: {
        partner: clientBasicDetails?.assignedTo?.partner?.value,
        manager: clientBasicDetails?.assignedTo?.manager?.value,
        senior_manager: clientBasicDetails?.assignedTo?.seniorManager?.value,
        currency: currencyDetails,
        accounting_in_charge:
          clientBasicDetails?.assignedTo?.accountingInCharge?.value,
        tax_in_charge: clientBasicDetails?.assignedTo?.taxInCharge?.value,
        audit_in_charge: clientBasicDetails?.assignedTo?.auditInCharge?.value,
        hr_in_charge: clientBasicDetails?.assignedTo?.hrInCharge?.value,
        branch: {
          name:
            clientBasicDetails?.assignedTo?.branch?.label === "Select.."
              ? ""
              : clientBasicDetails?.assignedTo?.branch?.label,
          id: clientBasicDetails?.assignedTo?.branch?.value,
        },
        client_group: {
          client_group_name:
            clientBasicDetails?.assignedTo?.clientGroup?.label === "Select.."
              ? ""
              : clientBasicDetails?.assignedTo?.clientGroup?.label,
          id: clientBasicDetails?.assignedTo?.clientGroup?.value,
        },
      },
      postal_address_details: {
        address_line1: clientBasicDetails?.postalAddress?.addressLine1,
        address_line2: clientBasicDetails?.postalAddress?.addressLine2,
        address: clientBasicDetails?.postalAddress?.address,
        city: clientBasicDetails?.postalAddress?.city?.label,
        state: clientBasicDetails?.postalAddress?.state?.label,
        pincode: clientBasicDetails?.postalAddress?.postalCode,
        country: clientBasicDetails?.postalAddress?.country?.label,
      },
      contact_details: {
        email: clientBasicDetails?.communication?.email?.trim(),
        mobile: clientBasicDetails?.communication?.mobile,
        home_phone: clientBasicDetails?.communication?.homePhone,
        work_phone: clientBasicDetails?.communication?.workPhone,
        fax: clientBasicDetails?.communication?.fax,
      },
      online_presence: {
        skype: clientBasicDetails?.onlinePresence?.skype,
        website: clientBasicDetails?.onlinePresence?.website,
        twitter: clientBasicDetails?.onlinePresence?.twitter,
        linkedin: clientBasicDetails?.onlinePresence?.linkedIn,
      },
    },
    registration_details: {
      corporate_id:
        registrationsInputDetails?.registrationDetails?.corporateIdOfTheCompany,
      director_of_partner_id:
        registrationsInputDetails?.registrationDetails?.directorOfPartnerId,
      tan: registrationsInputDetails?.registrationDetails?.tan,
      pan: registrationsInputDetails?.registrationDetails?.pan,
      tan_details: registrationsInputDetails?.registrationDetails?.tanDetails,
      pan_details: registrationsInputDetails?.registrationDetails?.panDetails,
      tin: registrationsInputDetails?.registrationDetails?.tin,
      service_tax_number:
        registrationsInputDetails?.registrationDetails?.serviceTaxNumber,
      gst_number: registrationsInputDetails?.gstDetails?.gstNumber,
      legal_name: registrationsInputDetails?.gstDetails?.legalName,
      trade_name: registrationsInputDetails?.gstDetails?.tradeName,
      registration_type:
        registrationsInputDetails?.gstDetails?.registrationType,
      registration_date:
        registrationsInputDetails?.gstDetails?.registrationDate,
      constituion_of_business:
        registrationsInputDetails?.gstDetails?.constitutionOfBusiness,
      company_status: registrationsInputDetails?.gstDetails?.companyStatus,
      registered_under_details: {
        iec_code: registrationsInputDetails?.registerdUnder?.iecCode,
        msme_udhyam_registration:
          registrationsInputDetails?.registerdUnder?.msmeUdhyamRegistration,
        startupindia: registrationsInputDetails?.registerdUnder?.startupIndia,
        iac_80_application:
          registrationsInputDetails?.registerdUnder?.iacApplication,
        gumasta: registrationsInputDetails?.registerdUnder?.gumasta,
        trademark: registrationsInputDetails?.registerdUnder?.trademark,
      },
      labour_law_details: {
        professional_tax_employee:
          registrationsInputDetails?.labourLaw?.professionalTaxEmployee,
        professional_tax_employer:
          registrationsInputDetails?.labourLaw?.professionalTaxEmployer,
        epfo: registrationsInputDetails?.labourLaw?.epfo,
      },
      others_details: {
        black_listed:
          registrationsInputDetails?.others?.blackListed?.value === "Yes"
            ? true
            : false,
        no_of_employees: registrationsInputDetails?.others?.numberOfEmployees,
        turnover_in_crores: registrationsInputDetails?.others?.turnoverInCrores,
        lie: registrationsInputDetails?.others?.lie,
        lin: registrationsInputDetails?.others?.lin,
        adhar_number: registrationsInputDetails?.others?.aadhaarNo,
        category_of_client:
          registrationsInputDetails?.others?.categoryOfClient?.value,
        business_nature: registrationsInputDetails?.others?.businessNature,
      },
    },
    tax_and_billing_details: {
      service_tax_details: {
        service_tax_registered:
          taxAndBillingDetails?.serviceDetails?.serviceTaxRegistered?.value ===
          "Yes"
            ? true
            : false,
        service_tax_period:
          taxAndBillingDetails?.serviceDetails?.serviceTaxPeriod?.value,
        service_tax_basis:
          taxAndBillingDetails?.serviceDetails?.serviceTaxBasis?.value,
        prepare_service_tax:
          taxAndBillingDetails?.serviceDetails?.prepareServiceTax?.value ===
          "Yes"
            ? true
            : false,
      },
      billing_details: {
        billing_entity:
          taxAndBillingDetails?.billingDetails?.billingEntity?.value === "Yes"
            ? true
            : false,
        charge_interest:
          taxAndBillingDetails?.billingDetails?.chargeInterest?.value === "Yes"
            ? true
            : false,
        interest_rate: taxAndBillingDetails?.billingDetails?.interestRate,
        target_hours: taxAndBillingDetails?.billingDetails?.targetHours,
        interest_period:
          taxAndBillingDetails?.billingDetails?.interestPeriod?.value,
        charge_monthly_disbursement:
          taxAndBillingDetails?.billingDetails?.chargeMonthlyDisbursement
            ?.value === "Yes"
            ? true
            : false,
        send_invoice_reminder:
          taxAndBillingDetails?.billingDetails?.sendInvoiceReminder?.value ===
          "Yes"
            ? true
            : false,
      },
    },
    profile_setup_details: {
      tax_year_end: profileSetupDetails?.profileSetup?.taxYearEnd?.value,
      client_type: profileSetupDetails?.profileSetup?.clientType?.value,
      clinet_type_subcategory:
        profileSetupDetails?.profileSetup?.clientTypeSubcategory?.value,
      no_of_employees: profileSetupDetails?.profileSetup?.noOfEmployees,
      in_business_since: profileSetupDetails?.profileSetup?.inBusinessSince
        ? formatDate(profileSetupDetails?.profileSetup?.inBusinessSince)
        : null,
      annual_accounts_month: profileSetupDetails?.annualAccountsMonth?.value,
      client_history: {
        client_from: profileSetupDetails?.clientHistory?.clientFrom
          ? formatDate(profileSetupDetails?.clientHistory?.clientFrom)
          : null,
        client_until: profileSetupDetails?.clientHistory?.clientUntil
          ? formatDate(profileSetupDetails?.clientHistory?.clientUntil)
          : null,
      },
    },
    lead_info_details: {
      lead_received_on: leadInfoDetails?.leadInfo?.leadReceivedOn
        ? formatDate(leadInfoDetails?.leadInfo?.leadReceivedOn)
        : null,
      lead_assigned_to: leadInfoDetails?.leadInfo?.leadAssignedTo?.value,
      demo_assigned_to: leadInfoDetails?.leadInfo?.demoAssignedTo?.value,
      meeting_assigned_to: leadInfoDetails?.leadInfo?.meetingAssignedTo?.value,
      meeting_time: leadInfoDetails?.leadInfo?.meetingTimewithDate
        ? moment(
            leadInfoDetails?.leadInfo?.meetingTimewithDate?.split(",").join("")
          ).format("DD-MM-YYYY, HH:mm:ss")
        : null,
      follow_up_date: leadInfoDetails?.leadInfo?.followUpDateWithTime
        ? moment(
            leadInfoDetails?.leadInfo?.followUpDateWithTime?.split(",").join("")
          ).format("DD-MM-YYYY, HH:mm:ss")
        : null,
      access_member_ids: leadInfoDetails?.leadInfo?.accessTeamMembersId,
      lead_details: {
        response: leadInfoDetails?.leadDetails?.response,
        stage: leadInfoDetails?.leadDetails?.stage?.value,
        lead_category: leadInfoDetails?.leadDetails?.leadCategary?.value,
        call_frequency: leadInfoDetails?.leadDetails?.callFrequency?.value
          ? leadInfoDetails?.leadDetails?.callFrequency?.value
          : "",
        source: leadInfoDetails?.leadDetails?.source,
        feedback: leadInfoDetails?.leadDetails?.feedback,
        lead_stage_unique_id: leadInfoDetails?.leadDetails?.uniqueId,
      },
      status: leadInfoDetails?.leadDetails?.status,
      company_details: {
        company_name: leadInfoDetails?.companyDetails?.companyName,
        contact_person_name: leadInfoDetails?.companyDetails?.contactPersonName,
        contact_number: leadInfoDetails?.companyDetails?.contactNumber,
        email: leadInfoDetails?.companyDetails?.emailId,
        gst_number_registered:
          leadInfoDetails?.companyDetails?.gstNumberRegister,
      },
      scope_of_work_details: {
        scope_of_work: leadInfoDetails?.scopeOfWorkDetails?.scopeOfWork,
      },
      attachments: leadInfoDetails?.attachments,
    },
  };
  return await postData(`master-module/customer/add`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}
// lead module
export async function getClientLeadListApi(
  data: GetLeadDirectoryListPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    searchData,
    limit,
    offset,
    stage,
    callFrequency,
    sortByLeadReceivedOn,
    sortByStage,
    sortByComment,
    leadAssignTo,
    filterStartDate,
    filterEndDate,
    role,
    leadCategory,
    turnOverFilter,
    migrateToFinacFilter,
    leadStatusFilter,
    clientCategory,
    scopeofWork,
    city,
  } = data;
  let finalParams = {
    search_term: searchData,
    limit: limit,
    offset: offset,
    role,
    lead_received_on_sorted_order: sortByLeadReceivedOn?.value,
    lead_received_on_start_date: filterStartDate
      ? reverseDate(filterStartDate)
      : null,
    lead_received_on_end_date: filterEndDate
      ? reverseDate(filterEndDate)
      : null,
    lead_assigned_to: leadAssignTo?.value,
    stage_sorted_order: sortByStage?.value,
    comments_sorted_order: sortByComment?.value,
    stage_id_filter: stage?.value,
    lead_category: leadCategory?.value,
    call_frequency: callFrequency?.value,
    turnover_in_crores: turnOverFilter?.value,
    finac_migration_status: migrateToFinacFilter ? "1" : null,
    status_filter: leadStatusFilter?.value,
    client_category: clientCategory?.value ? clientCategory?.label : "",
    scope_of_work: scopeofWork?.value ? scopeofWork?.label : "",
    city,
  };
  return await getData(`finac-crm/client-lead/list`, {
    params: {
      ...finalParams,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function getClientSortByThresholdLeadListApi(
  data: GetLeadDirectoryListPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    searchData,
    limit,
    offset,
    stage,
    callFrequency,
    sortByLeadReceivedOn,
    sortByStage,
    sortByComment,
    leadAssignTo,
    filterStartDate,
    filterEndDate,
    role,
    leadCategory,
    turnOverFilter,
    migrateToFinacFilter,
    leadStatusFilter,
    clientCategory,
    scopeofWork,
    city,
  } = data;
  let finalParams = {
    search_term: searchData,
    limit: limit,
    offset: offset,
    role,
    lead_received_on_sorted_order: sortByLeadReceivedOn?.value,
    lead_received_on_start_date: filterStartDate
      ? reverseDate(filterStartDate)
      : null,
    lead_received_on_end_date: filterEndDate
      ? reverseDate(filterEndDate)
      : null,
    lead_assigned_to: leadAssignTo?.value,
    stage_sorted_order: sortByStage?.value,
    comments_sorted_order: sortByComment?.value,
    stage_id_filter: stage?.value,
    lead_category: leadCategory?.value,
    call_frequency: callFrequency?.value,
    turnover_in_crores: turnOverFilter?.value,
    finac_migration_status: migrateToFinacFilter ? "1" : null,
    status_filter: leadStatusFilter?.value,
    client_category: clientCategory?.value ? clientCategory?.label : "",
    scope_of_work: scopeofWork?.value ? scopeofWork?.label : "",
    city,
  };
  return await getData(`finac-crm/lead/list`, {
    params: {
      ...finalParams,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function getSingleClientLeadDetailsApi(
  data: SingleClientLeadDirectoryDetails
): Promise<AxiosResponse> {
  const { token, clientCode } = data;
  return await getData(`finac-crm/client-lead`, {
    params: {
      client_code: clientCode,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}
export async function updateClientLeadDetailsApi(
  data: UpdateSingleClientLeadDirectoryDetails
): Promise<AxiosResponse> {
  const {
    token,
    clientCode,
    leadInfoDetails,
    comment_count,
    businessNature,
    lastComment,
    turnOver,
  } = data;
  const finalData = {
    client_code: clientCode,
    business_nature: businessNature,
    turnover_in_crores: turnOver,
    lead_info_details: {
      status: leadInfoDetails?.status,
      comment_count,
      last_comment: lastComment,
      access_member_ids: leadInfoDetails?.accessTeamMembersId,
      lead_received_on: leadInfoDetails?.leadInfo?.leadReceivedOn
        ? reverseDate(leadInfoDetails?.leadInfo?.leadReceivedOn)
        : null,
      lead_assigned_to: leadInfoDetails?.leadInfo?.leadAssignedTo?.value,
      demo_assigned_to: leadInfoDetails?.leadInfo?.demoAssignedTo?.value,
      meeting_assigned_to: leadInfoDetails?.leadInfo?.meetingAssignedTo?.value,
      finac_migration_status:
        leadInfoDetails?.leadInfo?.finacMigrateStatus === "Yes" ? true : false,
      meeting_time: leadInfoDetails?.leadInfo?.meetingTimewithDate
        ? moment(
            leadInfoDetails?.leadInfo?.meetingTimewithDate?.split(",").join("")
          ).format("DD-MM-YYYY, HH:mm:ss")
        : null,
      follow_up_date: leadInfoDetails?.leadInfo?.followUpDateWithTime
        ? moment(
            leadInfoDetails?.leadInfo?.followUpDateWithTime?.split(",").join("")
          ).format("DD-MM-YYYY, HH:mm:ss")
        : null,
      lead_details: {
        response: leadInfoDetails?.leadDetails?.response,
        stage: leadInfoDetails?.leadDetails?.stage?.value,
        lead_category: leadInfoDetails?.leadDetails?.leadCategary?.value,
        lead_stage_unique_id: leadInfoDetails?.leadDetails?.uniqueId,
        call_frequency: leadInfoDetails?.leadDetails?.callFrequency?.value
          ? leadInfoDetails?.leadDetails?.callFrequency?.value
          : "",
        source: leadInfoDetails?.leadDetails?.source,
        feedback: leadInfoDetails?.leadDetails?.feedback,
      },
      company_details: {
        company_name: leadInfoDetails?.companyDetails?.companyName,
        contact_person_name: leadInfoDetails?.companyDetails?.contactPersonName,
        contact_number: leadInfoDetails?.companyDetails?.contactNumber,
        email: leadInfoDetails?.companyDetails?.emailId,
        gst_number_registered:
          leadInfoDetails?.companyDetails?.gstNumberRegister,
      },
      scope_of_work_details: {
        scope_of_work: leadInfoDetails?.scopeOfWorkDetails?.scopeOfWork
          ? leadInfoDetails?.scopeOfWorkDetails?.scopeOfWork
          : [],
      },
      attachments: leadInfoDetails?.attachments,
    },
  };

  return await postData(`finac-crm/client-lead/update`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}
//  lead comments
export async function getLeadCommentsDetailsApi(
  data: GetLeadModuleCommentsPayload
): Promise<AxiosResponse> {
  const { token, client_code } = data;

  return await getData(`finac-crm/client-lead/comment/list`, {
    params: {
      client_code,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}
//  lead recording
export async function getLeadRecordingsDetailsApi(
  data: GetLeadModuleCommentsPayload
): Promise<AxiosResponse> {
  const { token, client_code } = data;

  return await getData(`finac-crm/lead/record/list`, {
    params: {
      client_code,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function addLeadCommentsApi(
  data: AddLeadModuleCommentPayload
): Promise<AxiosResponse> {
  const { token, client_code, comment } = data;
  const finalData = {
    comment,
    client_code,
  };
  return await postData(`finac-crm/client-lead/comment/add`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}
export async function addLeadRecordingApi(
  data: AddLeadModuleCommentPayload
): Promise<AxiosResponse> {
  const { token, client_code, recording_url } = data;
  const finalData = {
    client_code,
    recording_url,
  };
  return await postData(`finac-crm/lead/recording/add`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function getLeadHistoryApi(
  data: GetHistoryPayload
): Promise<AxiosResponse> {
  const { token, client_code } = data;

  return await getData(`history/client-lead-stage`, {
    params: {
      client_code,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function sendLeadEmailApi(
  data: SendEmailPayload
): Promise<AxiosResponse> {
  const { token, client_code, to_email } = data;
  const finalData = {
    to_email,
    client_code,
  };
  return await postData(`notification-module/lead-thank-you-email`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}
export async function sendClientEmailApi(
  data: SendEmailPayload
): Promise<AxiosResponse> {
  const { token, client_code, to_email } = data;
  const finalData = {
    to_email,
    client_code,
  };
  return await postData(
    `notification-module/client-thank-you-email`,
    finalData,
    {
      headers: {
        "Content-Type": "application/json",
        "user-token": token,
        "x-csrf-token": getCSRFToken(),
      },
    }
  );
}

export async function getLeadEmailHistoryApi(
  data: EmailHistoryPayload
): Promise<AxiosResponse> {
  const { token, client_code, email, template_name } = data;
  let finalData = {
    email,
    template_name,
    client_code,
  };
  return await getData(`notification-module/email/history`, {
    params: {
      ...finalData,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}
export async function getLeadStageCountApi(
  data: GetLeadDirectoryListPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    searchData,
    limit,
    offset,
    stage,
    callFrequency,
    sortByLeadReceivedOn,
    sortByStage,
    sortByComment,
    leadAssignTo,
    filterStartDate,
    filterEndDate,
    role,
    leadCategory,
    turnOverFilter,
    migrateToFinacFilter,
    leadStatusFilter,
    clientCategory,
    scopeofWork,
    city,
  } = data;
  let finalParams = {
    search_term: searchData,
    limit: limit,
    offset: offset,
    role,
    lead_received_on_start_date: filterStartDate
      ? reverseDate(filterStartDate)
      : null,
    lead_received_on_end_date: filterEndDate
      ? reverseDate(filterEndDate)
      : null,
    lead_assigned_to: leadAssignTo?.value,
    stage_id_filter: stage?.value,
    lead_category: leadCategory?.value,
    call_frequency: callFrequency?.value,
    turnover_in_crores: turnOverFilter?.value ? turnOverFilter?.value : "",
    finac_migration_status: migrateToFinacFilter ? "1" : null,
    status_filter: leadStatusFilter?.value,
    client_category: clientCategory?.value ? clientCategory?.label : "",
    scope_of_work: scopeofWork?.value ? scopeofWork?.label : "",
    city,
  };

  return await getData(`finac-crm/client-lead/stageCount`, {
    params: {
      ...finalParams,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function getLeadStageSortByStageCountApi(
  data: GetLeadDirectoryListPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    searchData,
    limit,
    offset,
    stage,
    callFrequency,
    sortByLeadReceivedOn,
    sortByStage,
    sortByComment,
    leadAssignTo,
    filterStartDate,
    filterEndDate,
    role,
    leadCategory,
    turnOverFilter,
    migrateToFinacFilter,
    leadStatusFilter,
    clientCategory,
    scopeofWork,
    city,
  } = data;
  let finalParams = {
    search_term: searchData,
    limit: limit,
    offset: offset,
    role,
    lead_received_on_start_date: filterStartDate
      ? reverseDate(filterStartDate)
      : null,
    lead_received_on_end_date: filterEndDate
      ? reverseDate(filterEndDate)
      : null,
    lead_assigned_to: leadAssignTo?.value,
    stage_id_filter: stage?.value,
    lead_category: leadCategory?.value,
    call_frequency: callFrequency?.value,
    turnover_in_crores: turnOverFilter?.value ? turnOverFilter?.value : "",
    finac_migration_status: migrateToFinacFilter ? "1" : null,
    status_filter: leadStatusFilter?.value,
    client_category: clientCategory?.value ? clientCategory?.label : "",
    scope_of_work: scopeofWork?.value ? scopeofWork?.label : "",
    city,
  };

  return await getData(`/finac-crm/lead/stageCount`, {
    params: {
      ...finalParams,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function downloadClientDirectoryReportsApi(
  data: DownloadClientDirectoryReportType
): Promise<AxiosResponse> {
  const { start_date, end_date, token } = data;

  return await getData(`master-module/client-list/download`, {
    params: {
      start_date,
      end_date,
    },
    responseType: "blob",
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function downloadLeadCommentsReportsApi(
  data: GetLeadDirectoryListPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    searchData,
    limit,
    offset,
    stage,
    callFrequency,
    sortByLeadReceivedOn,
    sortByStage,
    sortByComment,
    leadAssignTo,
    filterStartDate,
    filterEndDate,
    role,
    leadCategory,
    turnOverFilter,
    startDate,
    endDate,
    migrateToFinacFilter,
    leadStatusFilter,
    clientCategory,
    scopeofWork,
    city,
  } = data;

  let finalParams = {
    search_term: searchData,
    limit: limit,
    offset: offset,
    start_date: startDate,
    end_date: endDate,
    role,
    lead_received_on_sorted_order: sortByLeadReceivedOn?.value,
    lead_received_on_start_date: filterStartDate
      ? reverseDate(filterStartDate)
      : null,
    lead_received_on_end_date: filterEndDate
      ? reverseDate(filterEndDate)
      : null,
    lead_assigned_to: leadAssignTo?.value,
    stage_sorted_order: sortByStage?.value,
    comments_sorted_order: sortByComment?.value,
    stage: stage?.value,
    lead_category: leadCategory?.value,
    call_frequency: callFrequency?.value,
    turnover_in_crores: turnOverFilter?.value,
    finac_migration_status: migrateToFinacFilter ? "1" : null,
    status_filter: leadStatusFilter?.value,
    client_category: clientCategory?.value ? clientCategory?.label : "",
    scope_of_work: scopeofWork?.value ? scopeofWork?.label : "",
    city,
  };

  return await getData(`finac-crm/client-lead/comment/report/download`, {
    params: {
      ...finalParams,
    },
    responseType: "blob",
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}
export async function downloadLeadStageProgressReportsApi(
  data: GetLeadDirectoryListPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    searchData,
    limit,
    offset,
    stage,
    callFrequency,
    sortByLeadReceivedOn,
    leadAssignTo,
    filterStartDate,
    filterEndDate,
    role,
    leadCategory,
    turnOverFilter,
    startDate,
    endDate,
    migrateToFinacFilter,
    leadStatusFilter,
    clientCategory,
    scopeofWork,
  } = data;

  let finalParams = {
    search_term: searchData,
    limit: limit,
    offset: offset,
    start_date: startDate,
    end_date: endDate,
    role,
    lead_received_on_sorted_order: sortByLeadReceivedOn?.value,
    lead_received_on_start_date: filterStartDate
      ? reverseDate(filterStartDate)
      : null,
    lead_received_on_end_date: filterEndDate
      ? reverseDate(filterEndDate)
      : null,
    lead_assigned_to: leadAssignTo?.value,
    stage_id_filter: data?.stage_id_filter,
    lead_category: leadCategory?.value,
    call_frequency: callFrequency?.value,
    turnover_in_crores: turnOverFilter?.value,
    finac_migration_status: migrateToFinacFilter ? "1" : null,
    status_filter: leadStatusFilter?.value,
    client_category: clientCategory?.value ? clientCategory?.label : "",
    scope_of_work: scopeofWork?.value ? scopeofWork?.label : "",
  };

  return await getData(`finac-crm/client-lead/stage-progress/report/download`, {
    params: {
      ...finalParams,
    },
    responseType: "blob",
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

//client meet up checklist
export async function updateClientMeetUpCheckListApi(
  data: MeetingChecklistPayload
): Promise<AxiosResponse> {
  const {
    token,
    clientCode,
    scopeOfWork,
    turnOverYearly,
    numberOfSalesEntries,
    numberOfPurchaseEntries,
    numberOfBankEntries,
    bussinessType,
    workingHoursInClientPlace,
    currentDataInWhichSoftware,
    remark,
    startingMonthOfAccounting,
    anyOldWork,
    nameOrSignedOfMeetUpBy,
    meetUpComplete,
  } = data;

  const finalData = {
    client_code: clientCode,
    client_meet_up_details: {
      scope_of_work: scopeOfWork,
      turnover_yearly: turnOverYearly,
      number_of_sales_entries: numberOfSalesEntries,
      number_of_purchase_entries: numberOfPurchaseEntries,
      number_of_banking_entries: numberOfBankEntries,
      business_type: bussinessType,
      working_hours_in_client_place: workingHoursInClientPlace,
      current_software: currentDataInWhichSoftware,
      remarks: remark,
      start_month_of_accounting: startingMonthOfAccounting,
      old_work: anyOldWork,
      meet_up_by: nameOrSignedOfMeetUpBy,
      meet_up_completed: meetUpComplete,
    },
  };

  return await postData(`finac-crm/lead/meet-up/checklist/update`, finalData, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}
